import { Injectable } from '@angular/core';

import { ApiService }                from '../api/api.service';
import { CodeTypeModel }             from '../core/models/code-type';
import { GroupModel }                from '../core/models/group';
import { SessionService }            from '../sessions/session.service';
import { StreamSearchService }       from '../api/stream-search.service';
import { TableQueryParameters }      from '../table/types/table-query-parameters';
import { UserModel }                 from './typings/user';
import { UserNotificationTypeModel } from './typings/user.notification-type';
import { UserSettingsModel }         from './typings/user.settings';
import { UsersFilterModel }          from './typings/users.filter';
import { UsersMechanicModel }        from './typings/users.mechanic';

@Injectable({
  providedIn: 'root'
})
export class UsersService {
  public broadcastKey        = 'filterUsers';
  public mobileBroadcastKey  = 'filterMobileUsers';
  public mobileTableCacheKey = 'mobileUsers';
  public tableCacheKey       = 'users';

  constructor (
    private apiService         : ApiService,
    private sessionService     : SessionService,
    private streamSearchService: StreamSearchService,
  ) {}

  changePassword ( passwordInfo : any ) : Promise<any> {
    return this.apiService.post('/users/change_password', passwordInfo);
  }

  createMobileUser ( user : UserModel ) : Promise<UserModel> {
    return this.apiService.post('/mobile_users', user, 'mobile_user');
  }

  createUser ( user : UserModel ) : Promise<UserModel> {
    return this.apiService.post('/users', user, 'user');
  }

  deleteMobileUser ( user : UserModel ) : Promise<any> {
    return this.apiService.delete('/mobile_users', user.id);
  }

  deleteUser ( user : UserModel ) : Promise<any> {
    return this.apiService.delete('/user', user.id);
  }

  get ( filtering : UsersFilterModel, params : TableQueryParameters ) : Promise<Array<UserModel>> {
    return this.apiService.stream('/filters', [
        filtering.query || '*',
        'users',
        params.offset    || 0,
        params.pageSize  || 11,
        params.field     || 'username',
        params.direction || 'asc',
        false
      ]);
  }

  getCount ( filter = '' ) : Promise<number> {
    return this.apiService.get('/filters/count', [
        filter || '*',
        'users',
        false
      ]);
  }

  getGroups () : Promise<Array<GroupModel>> {
    return this.apiService.get('/groups', [
        '*',
        '*'
      ]);
  }

  getJobCardOptions () : Promise<Array<CodeTypeModel>> {
    return Promise.resolve([
        {
          code          : true,
          language_label: 'JS_SPACE.USER_SETTINGS.ALWAYS'
        },
        {
          code          : false,
          language_label: 'JS_SPACE.USER_SETTINGS.NEVER'
        },
        {
          code          : null,
          language_label: 'JS_SPACE.USER_SETTINGS.ASK_ME_EVERY_TIME'
        }
      ]);
  }

  getMobile ( filtering : UsersFilterModel, params : TableQueryParameters ) : Promise<Array<UserModel>> {
    return this.apiService.stream('/filters', [
        filtering.query || '*',
        'mobile_users',
        params.offset    || 0,
        params.pageSize  || 11,
        params.field     || 'username',
        params.direction || 'asc',
        false
      ]);
  }

  getMechanic ( id : string ) : Promise<UsersMechanicModel> {
    return this.apiService.get('/mechanic', id);
  }

  getMobileCount ( filter = '' ) : Promise<number> {
    return this.apiService.get('/filters/count', [
        filter || '*',
        'mobile_users',
        false
      ]);
  }

  getMobileNotificationTypes ( userId : string ) : Promise<Array<UserNotificationTypeModel>> {
    return this.apiService.get('/user_mobile_notification_types', userId);
  }

  getQuote () : Promise<any> {
    return this.apiService.get('/users/quote_for_user');
  }

  getStateTypes () : Promise<Array<CodeTypeModel>> {
    return this.apiService.get('/system/user_state_types');
  }

  getTerminals () : Promise<Array<any>> {
    return this.apiService.get('/pos_terminals/tnp_terminal_list', [
        0,
        '*',
        'pos_terminal_name',
        'asc'
      ]);
  }

  getUser ( id = this.sessionService.user().id ) : Promise<UserModel> {
    return this.apiService.get('/user', id);
  }

  resetTwoFactorAuth ( user : UserModel ) : Promise<any> {
    return this.apiService.post('/user/reset_two_factor_auth_validity', {
        id: user.id
      });
  }

  searchMechanics ( typed : string ) : Promise<Array<UsersMechanicModel>> {
    return this.streamSearchService.search('mechanics', typed) as Promise<Array<UsersMechanicModel>>;
  }

  updateMobileUser ( user : UserModel ) : Promise<UserModel> {
    return this.apiService.patch('/mobile_user', user, 'mobile_user');
  }

  updateMobileNotificationType ( notification : UserNotificationTypeModel, userId ?: string ) : Promise<any> {
    return this.apiService.patch(`/user_mobile_notification_types/${ notification.id }/${ userId }`, {
        enabled: notification.subscribed,
        id     : notification.id,
        user_id: userId
      });
  }

  updateUser ( user : UserModel | UserSettingsModel ) : Promise<UserModel> {
    return this.apiService.patch('/user', user, 'user');
  }
}
